/**
 * TaskRow: a single dense row representing one task in the Director cockpit.
 *
 * SAKME-TASKSTAB-COCKPIT-SIMPLIFY-1 (2026-04-08).
 *
 * Replaces the per-category 4-column kanban (TaskStreamLane) with a flat
 * single-row layout that fits hundreds of tasks on one screen. Every row
 * exposes exactly three always-visible action buttons:
 *
 *   ▶ ACTIVATE  — start work on the task (PATCH status to in_progress)
 *   ✓ DONE      — mark task complete (PATCH status to done)
 *   ▤ PROMPT    — open the dispatch prompt for view/edit
 *
 * No hover-to-reveal, no overflow menus, no per-status branching. The
 * Director sees the same three buttons on every actionable row.
 *
 * Done and cancelled tasks render dimmed without action buttons.
 *
 * The whole row is clickable to open the detail panel; the buttons stop
 * propagation so they don't double-fire.
 */

import { CheckCircle2, Loader2, Play, FileText, Star, Bot, User } from 'lucide-react';
import type { TaskItem, TaskPriority } from '@/api/types';
import { getTaskOwner } from '@/lib/taskOwner';

const PRIORITY_COLOR: Record<TaskPriority, string> = {
  critical: '#ef4444',
  high: '#f97316',
  medium: '#eab308',
  low: '#6b7280',
};

const PRIORITY_LABEL: Record<TaskPriority, string> = {
  critical: 'CRIT',
  high: 'HIGH',
  medium: 'MED',
  low: 'LOW',
};

interface TaskRowProps {
  task: TaskItem;
  onSelect: (task: TaskItem) => void;
  onActivate: (task: TaskItem) => void | Promise<void>;
  onComplete: (task: TaskItem) => void | Promise<void>;
  onOpenPrompt: (task: TaskItem) => void | Promise<void>;
  loading?: boolean;
  mirroredFromDecisions?: boolean;
}

export function TaskRow({
  task,
  onSelect,
  onActivate,
  onComplete,
  onOpenPrompt,
  loading = false,
  mirroredFromDecisions = false,
}: TaskRowProps) {
  const isDone = task.status === 'done' || task.status === 'cancelled';
  const isActive = task.status === 'in_progress';
  const isBlocked = task.status === 'blocked';
  const isReview = task.status === 'review';

  // SAKME-TASKSTAB-COCKPIT-SIMPLIFY-1 phase 2:
  // - Show PROMPT button only when dispatch_prompt is non-empty (honest
  //   signal; rows that can't deliver a prompt don't promise one)
  // - Show a ⭐ star marker when the task has the 'top-12' tag (the
  //   Director's active working set)
  const hasPrompt = !!(task.dispatch_prompt && task.dispatch_prompt.trim());
  const isTopTwelve = Array.isArray(task.tags) && task.tags.includes('top-12');
  const owner = getTaskOwner(task);

  // Status visual: a small left-border accent that telegraphs state
  let statusBorder = 'border-l-2 border-gray-700';
  if (isActive) statusBorder = 'border-l-2 border-[#39FF14]';
  else if (isReview) statusBorder = 'border-l-2 border-yellow-400';
  else if (isBlocked) statusBorder = 'border-l-2 border-red-500';
  else if (isDone) statusBorder = 'border-l-2 border-green-700/40';

  return (
    <div
      data-testid={`task-row-${task.id}`}
      onClick={() => onSelect(task)}
      className={`
        flex items-center gap-2 px-3 py-1.5 rounded text-sm font-mono cursor-pointer
        transition-colors hover:bg-gray-800/40
        ${statusBorder}
        ${isDone ? 'opacity-50' : ''}
      `}
    >
      {/* Top-12 working-set marker */}
      {isTopTwelve && (
        <span
          className="shrink-0 w-4 flex items-center justify-center"
          title="TOP 12 — in the active working set"
          data-testid={`top-12-star-${task.id}`}
        >
          <Star
            className="w-3.5 h-3.5 text-yellow-300"
            fill="currentColor"
          />
        </span>
      )}
      {!isTopTwelve && <span className="shrink-0 w-4" />}

      {/* Priority badge */}
      <span
        className="text-[9px] font-bold px-1.5 py-0.5 rounded uppercase shrink-0 w-12 text-center"
        style={{
          color: PRIORITY_COLOR[task.priority],
          backgroundColor: `${PRIORITY_COLOR[task.priority]}1a`,
        }}
      >
        {PRIORITY_LABEL[task.priority]}
      </span>

      {/* task_id */}
      {task.task_id && (
        <span className="text-[10px] text-gray-500 shrink-0 w-36 truncate flex items-center gap-1">
          <span className="truncate">{task.task_id}</span>
          {mirroredFromDecisions && (
            <span
              className="text-purple-300"
              title="Mirrored from DECISIONS"
              aria-label="Mirrored from DECISIONS"
            >
              ↗
            </span>
          )}
        </span>
      )}

      {/* Title */}
      <span className="flex-1 truncate text-gray-200">{task.title}</span>

      {/* Owner chip — bot icon for agents, user for humans, hidden when
          unassigned. Same heuristic as the Kanban card so the two views can't
          disagree. (BOARD-TEAM-OWNERSHIP-UX-1) */}
      {owner.kind !== 'unassigned' && (
        <span
          data-testid={`task-row-owner-${task.id}`}
          data-owner-kind={owner.kind}
          title={owner.kind === 'agent' ? `Agent: ${owner.handle}` : `Assigned to ${owner.handle}`}
          className="hidden md:inline-flex items-center gap-1 text-[10px] text-gray-400 bg-gray-800/50 rounded px-1.5 py-0.5 shrink-0 max-w-[140px]"
        >
          {owner.kind === 'agent'
            ? <Bot className="w-2.5 h-2.5 text-[#39FF14]/60 shrink-0" />
            : <User className="w-2.5 h-2.5 shrink-0" />
          }
          <span className="truncate">{owner.handle}</span>
        </span>
      )}

      {/* Status pill (compact) */}
      {!isDone && (
        <span
          className={`text-[9px] uppercase tracking-wider shrink-0 px-1.5 py-0.5 rounded ${
            isActive
              ? 'text-[#39FF14] bg-green-900/20'
              : isReview
                ? 'text-yellow-300 bg-yellow-900/20'
                : isBlocked
                  ? 'text-red-400 bg-red-900/20'
                  : 'text-gray-500 bg-gray-800/40'
          }`}
        >
          {task.status === 'in_progress' ? 'active' : task.status}
        </span>
      )}

      {/* Three action buttons — always visible on actionable rows */}
      {!isDone && (
        <div className="flex items-center gap-1 shrink-0">
          {/* ACTIVATE */}
          <button
            type="button"
            data-testid={`activate-btn-${task.id}`}
            disabled={loading || isActive}
            title={isActive ? 'Already active' : 'Activate (start work)'}
            onClick={(e) => {
              e.stopPropagation();
              if (!loading && !isActive) void onActivate(task);
            }}
            className="flex items-center gap-1 text-[10px] font-bold uppercase px-2 py-0.5 rounded
              border border-green-700/50 text-green-300 hover:bg-green-900/30
              disabled:opacity-30 disabled:cursor-not-allowed"
          >
            {loading ? (
              <Loader2 className="w-3 h-3 animate-spin" />
            ) : (
              <Play className="w-3 h-3" />
            )}
            <span>ACTIVATE</span>
          </button>

          {/* DONE */}
          <button
            type="button"
            data-testid={`done-btn-${task.id}`}
            disabled={loading}
            title="Mark as done"
            onClick={(e) => {
              e.stopPropagation();
              if (!loading) void onComplete(task);
            }}
            className="flex items-center gap-1 text-[10px] font-bold uppercase px-2 py-0.5 rounded
              border border-cyan-700/50 text-cyan-300 hover:bg-cyan-900/30
              disabled:opacity-30 disabled:cursor-not-allowed"
          >
            <CheckCircle2 className="w-3 h-3" />
            <span>DONE</span>
          </button>

          {/* PROMPT — only shown when dispatch_prompt is actually set */}
          {hasPrompt && (
            <button
              type="button"
              data-testid={`prompt-btn-${task.id}`}
              title="View dispatch prompt"
              onClick={(e) => {
                e.stopPropagation();
                void onOpenPrompt(task);
              }}
              className="flex items-center gap-1 text-[10px] font-bold uppercase px-2 py-0.5 rounded
                border border-purple-700/50 text-purple-300 hover:bg-purple-900/30"
            >
              <FileText className="w-3 h-3" />
              <span>PROMPT</span>
            </button>
          )}
        </div>
      )}

      {/* Done indicator */}
      {isDone && (
        <span className="text-[10px] text-green-500 shrink-0 flex items-center gap-1">
          <CheckCircle2 className="w-3 h-3" />
          DONE
        </span>
      )}
    </div>
  );
}
